import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteParticipantPitch,
  fetchParticipantById,
  updateParticipantProfile,
} from "@/lib/api/participants";
import {
  clearParticipantSession,
  getParticipantSession,
} from "@/lib/participant-session";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Field } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { VideoAvatar } from "@/components/ui/VideoAvatar";
import { Loader, InlineAlert } from "@/components/ui/States";
import { Badge } from "@/components/ui/Badge";
import { ParticipantShell } from "@/components/layout/ParticipantShell";
import { TelegramConnectBlock } from "@/components/participant/TelegramConnectBlock";
import { NotificationPreferencesBlock } from "@/components/participant/NotificationPreferencesBlock";

export function ProfileSettingsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const session = slug ? getParticipantSession(slug) : null;

  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  const { data: participant, isLoading } = useQuery({
    queryKey: ["participant", session?.participantId],
    queryFn: () => fetchParticipantById(session?.participantId ?? ""),
    enabled: Boolean(session?.participantId),
  });

  const [form, setForm] = useState<{
    looking_for: string;
    can_help_with: string;
    contact_for_matches: string;
  }>({ looking_for: "", can_help_with: "", contact_for_matches: "" });
  const [saved, setSaved] = useState(true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingReRecord, setConfirmingReRecord] = useState(false);

  useEffect(() => {
    if (participant) {
      setForm({
        looking_for: participant.looking_for ?? "",
        can_help_with: participant.can_help_with ?? "",
        contact_for_matches: participant.contact_for_matches ?? "",
      });
      setSaved(true);
    }
  }, [participant]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateParticipantProfile(session?.participantId ?? "", {
        looking_for: form.looking_for.trim(),
        can_help_with: form.can_help_with.trim(),
        contact_for_matches: form.contact_for_matches.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participant", session?.participantId] });
      setSaved(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteParticipantPitch(session?.participantId ?? ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participant", session?.participantId] });
      if (slug) clearParticipantSession(slug);
      navigate(`/e/${slug}`, { replace: true });
    },
  });

  if (!session) return null;
  if (isLoading) {
    return (
      <ParticipantShell hideNav>
        <Loader label="Loading profile..." />
      </ParticipantShell>
    );
  }

  const update = (key: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const hasVideo = Boolean(participant?.video_storage_path);
  const pitchPath = `/e/${slug}/pitch`;

  return (
    <ParticipantShell>
      <div className="animate-fade-in">
        <button
          type="button"
          onClick={() => navigate("/me")}
          className="mb-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#475569] hover:text-[#2563eb]"
        >
          ← Account
        </button>
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">
          Profile · {slug}
        </h1>
        <p className="mt-1 text-[13px] text-[#6c757d]">
          These settings apply to this event only. Goal text edits update your matches in the background.
        </p>

        <Card padding="md" className="mt-5">
          <div className="flex items-center gap-4">
            <VideoAvatar
              name={participant?.name ?? session.name}
              size={64}
              bordered
              videoStoragePath={participant?.video_storage_path}
              eventId={session.eventId}
              viewerParticipantId={session.participantId}
              showPlayBadge
            />
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-bold text-[#1a1a1a]">
                {participant?.name ?? session.name}
              </p>
              <p className="text-[12px] text-[#6c757d]">
                {[participant?.role, participant?.company].filter(Boolean).join(" · ") ||
                  session.email}
              </p>
              {participant?.pitch_status ? (
                <Badge tone={pitchTone(participant.pitch_status)} className="mt-1">
                  {participant.pitch_status}
                </Badge>
              ) : null}
            </div>
          </div>
        </Card>

        <TelegramConnectBlock participantId={session.participantId} />
        <NotificationPreferencesBlock />

        <div className="mt-5 flex flex-col gap-4">
          <Field
            label="Contact for matches"
            hint="Shared only with people you mutually match with."
          >
            <Input
              value={form.contact_for_matches}
              onChange={(e) => update("contact_for_matches", e.target.value)}
              placeholder="@telegram_user or +351 912 345 678"
            />
          </Field>
          <Field
            label="Looking for"
            hint="Edits update your feed matches in the background within a few minutes."
          >
            <Textarea
              value={form.looking_for}
              onChange={(e) => update("looking_for", e.target.value)}
              rows={3}
            />
          </Field>
          <Field
            label="Can help with"
            hint="Edits update your feed matches in the background within a few minutes."
          >
            <Textarea
              value={form.can_help_with}
              onChange={(e) => update("can_help_with", e.target.value)}
              rows={3}
            />
          </Field>
        </div>

        {hasVideo ? (
          <Card padding="md" className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
              Your pitch
            </p>
            <p className="mt-1 text-[14px] text-[#1a1a1a]">Status: {participant?.pitch_status}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => setConfirmingReRecord(true)}
            >
              ↻ Re-record Pitch
            </Button>
          </Card>
        ) : (
          <Card tone="flagged" padding="md" className="mt-5">
            <p className="text-[13px] text-[#78350f]">
              No pitch recorded yet.{" "}
              <a
                href={`/e/${slug}/pitch`}
                className="font-semibold underline"
              >
                Record now →
              </a>
            </p>
          </Card>
        )}

        <Button
          onClick={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          disabled={saved}
          className="mt-5"
          size="md"
        >
          {saveMutation.isPending ? "Saving..." : saved ? "Saved" : "Save Changes"}
        </Button>

        <div className="mt-8 border-t border-[#e2e8f0] pt-6">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#991b1b]">Danger zone</p>
          {!confirmingDelete ? (
            <>
              <p className="mt-2 text-[13px] text-[#475569]">
                Deleting your pitch removes you from the feed and cancels all your matches.
              </p>
              <Button
                variant="destructive"
                onClick={() => setConfirmingDelete(true)}
                className="mt-3"
                size="md"
              >
                Delete My Pitch
              </Button>
            </>
          ) : (
            <Card tone="danger" padding="md" className="mt-3">
              <p className="text-[14px] font-bold text-[#991b1b]">Are you sure?</p>
              <p className="mt-1 text-[13px] text-[#991b1b]">
                Your profile disappears from the feed, your matches are cancelled, and the video is deleted.
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="destructive"
                  onClick={() => deleteMutation.mutate()}
                  loading={deleteMutation.isPending}
                  fullWidth
                  size="md"
                >
                  {deleteMutation.isPending ? "Deleting..." : "Yes, delete my pitch"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setConfirmingDelete(false)}
                  fullWidth
                  size="md"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmingReRecord}
        onClose={() => setConfirmingReRecord(false)}
        onConfirm={() => navigate(pitchPath)}
        title="Re-record your pitch?"
        description="Your current pitch will be replaced."
        icon="↻"
        confirmLabel="Yes, re-record"
        cancelLabel="Keep current pitch"
        body={
          <InlineAlert tone="info">
            <p className="font-semibold">What happens next</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              <li>The new pitch goes through AI moderation again</li>
              <li>Takes a few minutes</li>
              <li>Existing likes are kept — matches re-evaluate</li>
            </ul>
          </InlineAlert>
        }
      />
    </ParticipantShell>
  );
}

function pitchTone(status: string) {
  if (status === "approved") return "success" as const;
  if (status === "flagged") return "warning" as const;
  if (status === "rejected") return "danger" as const;
  if (status === "submitted") return "info" as const;
  return "neutral" as const;
}
